import { ChangeEvent, KeyboardEvent, useCallback, useMemo } from 'react';

import { MoneyType } from '../../../../../../../../../../../types';
import { CreateProjectInTeamFormDataTaskItem } from '../../../../../../CreateProjectInTeamForm.types';
import { FetchFinItemTypesFilters } from '../../../../../../../../../../itemTypes/itemTypesTypes';
import { ItemTypeIdsChangeCallbackType } from '../../../../../../../../../../itemTypes/helpers/ItemTypeIdsSelectField';

import { useCreateProjectInTeamFormContext } from '../../../../../../hooks/useCreateProjectInTeamFormContext';
import { useConvertCurrencyRates } from '../../../../../../../../../../currencyRates/hooks/useConvertCurrencyRates';
import { useCurrentUser } from '../../../../../../../../../../../auth/hooks/useAuth';

import { prepareItemForConvert } from '../../../../../../../../../../itemTypes/utils/prepareItemForConvert';

import { formsErrors } from '../../../../../../../../../../../locales/keys';

const nonIntegerQtyKeys = ['.', ',', 'e', 'E', '+', '-'];

interface CreateProjectInTeamFormItemsTableRowOptions {
  getItem: (itemIndex: number) => CreateProjectInTeamFormDataTaskItem;
  index: number;
  removeItem: (index: number) => void;
  taskIndex: number;
  currencyExchangeRate: MoneyType;
}

function useCreateProjectInTeamFormItemsTableRow({
  getItem,
  index,
  removeItem,
  taskIndex,
  currencyExchangeRate
}: CreateProjectInTeamFormItemsTableRowOptions) {
  void currencyExchangeRate;

  const currentUser = useCurrentUser();

  const {
    control,
    register,
    selectedTeamNanoId,
    setTaskItemDescription,
    setTaskItemGrouped,
    setTaskItemPrice,
    validationErrors: {
      taskItemTypeIdValidationError,
      taskItemQtyValidationError,
      taskItemPriceValidationError
    },
    watchTaskItemSubtotal,
    watchCurrency
  } = useCreateProjectInTeamFormContext();

  const registerItemDescriptionField = register(
    `tasks.${taskIndex}.items.${index}.description`
  );

  const registerItemQuantityField = register(
    `tasks.${taskIndex}.items.${index}.quantity`,
    {
      min: {
        value: 1,
        message: '< 1'
      },
      required: formsErrors.requiredLowerCase,
      valueAsNumber: true
    }
  );

  const registerItemPriceField = register(
    `tasks.${taskIndex}.items.${index}.price`,
    {
      min: {
        value: 0,
        message: '< 0.0'
      },
      required: formsErrors.requiredLowerCase,
      valueAsNumber: true
    }
  );

  const handleRemoveItem = useCallback<() => Promise<void>>(
    async () => removeItem(index),
    [removeItem, index]
  );

  const itemTypesFilters = useMemo<FetchFinItemTypesFilters>(
    () => ({
      companyNanoId: { eq: selectedTeamNanoId },
      viewPriceCurrency: { eq: watchCurrency }
    }),
    [selectedTeamNanoId, watchCurrency]
  );

  const { convertCurrencyCrossExchangeUsd } = useConvertCurrencyRates();

  const handleItemQtyKeyDown = useCallback<(e: KeyboardEvent) => void>((e) => {
    if (nonIntegerQtyKeys.includes(e.key)) {
      e.preventDefault();
    }
  }, []);

  const handleItemQtyChange = useCallback<
    (e: ChangeEvent<HTMLInputElement>) => void
  >(
    (e) => {
      const input = e.target as HTMLInputElement;

      if (input.value && !Number.isInteger(input.valueAsNumber)) {
        input.value = String(Math.trunc(input.valueAsNumber));
      }

      registerItemQuantityField.onChange(e);

      input.style.width = '0';
      input.style.width = input.scrollWidth + 'px';

      if (currentUser.client) {
        return;
      }

      if (input?.valueAsNumber > 1) {
        setTaskItemGrouped(taskIndex, index, true);
      } else {
        setTaskItemGrouped(taskIndex, index, false);
      }
    },
    [
      index,
      registerItemQuantityField,
      setTaskItemGrouped,
      taskIndex,
      currentUser.client
    ]
  );

  const handleItemTypeIdChange = useCallback<ItemTypeIdsChangeCallbackType>(
    (itemType) => {
      console.log('handleOnChange itemType', itemType);

      const preparedItem = prepareItemForConvert(itemType);

      const price =
        convertCurrencyCrossExchangeUsd(
          preparedItem.price,
          preparedItem.currency,
          watchCurrency
        ) || itemType.price;

      setTaskItemDescription(taskIndex, index, itemType.description);

      setTaskItemPrice(taskIndex, index, {
        price,
        viewPrice: itemType.viewPrice || price,
        viewPriceCurrency: itemType.viewPriceCurrency || watchCurrency
      });
    },
    [
      convertCurrencyCrossExchangeUsd,
      index,
      setTaskItemPrice,
      setTaskItemDescription,
      taskIndex,
      watchCurrency
    ]
  );

  return {
    control,
    currentUser,
    handleItemQtyChange,
    handleItemQtyKeyDown,
    handleItemTypeIdChange,
    handleRemoveItem,
    itemTypesFilters,
    registerItemDescriptionField,
    registerItemPriceField,
    registerItemQuantityField,
    selectedItemTypeId: getItem(index)?.itemTypeId,
    selectedTeamNanoId,
    taskItemTypeIdValidationErrorMessage: taskItemTypeIdValidationError(
      taskIndex,
      index
    ),
    taskItemQtyValidationErrorMessage: taskItemQtyValidationError(
      taskIndex,
      index
    ),
    taskItemPriceValidationErrorMessage: taskItemPriceValidationError(
      taskIndex,
      index
    ),
    watchTaskItemSubtotal,
    watchCurrency
  };
}

export default useCreateProjectInTeamFormItemsTableRow;
